import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from './lib/firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { router } from './router';

interface User {
  uid: string;
  displayName: string | null;
  email: string | null;
}

export interface AuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  isImpersonating: boolean;
  awaitAuthState: () => Promise<AuthState>;
  login: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<void>;
  logout: () => void;
}

type AuthState = Pick<
  AuthContext,
  'isAuthenticated' | 'isLoading' | 'user' | 'isImpersonating'
>;

const AuthContext = createContext<AuthContext | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    isImpersonating: false,
  });
  const { isAuthenticated, isLoading, user, isImpersonating } = authState;

  const login = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;
    setAuthState({
      isAuthenticated: true,
      isLoading: false,
      user,
      isImpersonating: false,
    });
  };

  const logout = () => {
    signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user,
          isImpersonating: false,
        });
      } else {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          isImpersonating: false,
        });
      }
      router.invalidate();
    });
    return () => unsubscribe();
  }, []);

  const awaitAuthState = async (): Promise<AuthState> => {
    return new Promise((resolve) => {
      let authState: AuthState;
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        if (user) {
          authState = {
            isAuthenticated: true,
            isLoading: false,
            user,
            isImpersonating: false,
          };
        } else {
          authState = {
            isAuthenticated: false,
            isLoading: false,
            user: null,
            isImpersonating: false,
          };
        }
        resolve(authState);
      });
    });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        isImpersonating,
        awaitAuthState,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
